/**
 * Redaction for the ApiClient's native request logging (Xcode / logcat / device consoles keep it).
 *
 * Pure on purpose — no db, no Capacitor — so it is unit-tested on its own
 * (`__tests__/logRedaction.test.ts`).
 *
 * Rules:
 * - `/ical/*` requests (secret calendar feed URLs in the body, calendar text in the answer): log the
 *   method and the path only. No query string, body, answer or error detail.
 * - Everywhere else: bodies and answers are logged with values under keys that can hold a secret
 *   (`url`, `token`, `secret`, `password`, `apiKey`, `refresh_token`, `authorization`, ...) masked.
 */

const SENSITIVE_KEY = /url|token|secret|password|passwd|api[-_]?key|authorization|credential|cookie/i

export const REDACTED = "[redacted]"

/** True for the calendar proxy / feed routes, whatever the host or prefix in front of them. */
export function isIcalPath(urlOrPath: string): boolean {
  const path = urlOrPath.replace(/^[a-zA-Z][a-zA-Z\d+.-]*:\/\/[^/]+/, "").split(/[?#]/)[0] ?? ""
  return /(^|\/)ical(\/|$)/i.test(path)
}

/** The URL without query string and fragment (they can carry tokens). */
export function stripQuery(url: string): string {
  return url.split(/[?#]/)[0] ?? url
}

export function redactValue(value: unknown, depth = 0): unknown {
  if (depth > 6) return "[too deep]"
  if (Array.isArray(value)) return value.map((v) => redactValue(v, depth + 1))
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [
        k,
        SENSITIVE_KEY.test(k) ? REDACTED : redactValue(v, depth + 1),
      ]),
    )
  }
  return value
}

/** A body or answer as one short log string, secrets masked. */
export function summarizeRedacted(data: unknown, maxLen = 400): string {
  if (data === undefined) return "undefined"
  if (data === null) return "null"
  let value = data
  if (typeof data === "string") {
    try {
      value = JSON.parse(data)
    } catch {
      // Not JSON: it could be anything (form data, a feed), so only its size is logged.
      return `[${data.length} chars]`
    }
  }
  try {
    const json = JSON.stringify(redactValue(value))
    return json.length > maxLen ? `${json.slice(0, maxLen)}...` : json
  } catch {
    return "[unserializable]"
  }
}
